import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useToast } from "../../components";
import { PartnerOfferingLocation, Template } from "../../data/partner_offering";
import { useQueryString } from "../../hooks";
import { isPartnerLocation } from "@/data/api";

/**
 *
 * @param offeringLocations
 * Some partners will redirect to partners web catalog page with a locationID from their website
 * This hook will ensure to update city on url from this locationID
 */
export function useAppendCityFromURLLocationSpaceOrOfferingID(
  offeringLocations: PartnerOfferingLocation[],
  template: Template
) {
  const queryParams = useQueryString();
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const [toastShowed, setToastShowed] = useState(false);

  const offeringTypeString = useMemo(
    () =>
      template === Template.DailyAccessV2 || template === Template.AddOnV2
        ? "day pass"
        : template === Template.DailyOffice
        ? "day office"
        : "",
    [template]
  );

  useEffect(() => {
    if (queryParams.locationID && !queryParams.city && offeringLocations) {
      const foundOfferingLocation = offeringLocations.find(
        (loc) => loc.location.id === queryParams.locationID
      );

      if (foundOfferingLocation) {
        const search = new URLSearchParams(location.search);
        search.append("city", foundOfferingLocation.location.city);
        history.replace(
          `${location.pathname}?${search.toString()}`,
          location.state
        );
      } else if (!toastShowed) {
        toast.warning({
          message: `The pre-selected location does not have ${offeringTypeString}`,
        });
        setToastShowed(true);
      }
    }

    // Day pass can have spaceID in the url and not locationID.
    // This check will find the city from spaceID for daypasses
    if (
      queryParams.spaceID &&
      !queryParams.city &&
      offeringLocations &&
      !queryParams.locationID
    ) {
      const foundOfferingLocation = offeringLocations.find(
        (loc) => loc.spaceID === queryParams.spaceID
      );
      if (foundOfferingLocation) {
        const search = new URLSearchParams(location.search);
        search.append("city", foundOfferingLocation.location.city);
        history.replace(
          `${location.pathname}?${search.toString()}`,
          location.state
        );
      }
    }

    // Day office can have partnerOfferingLocationID in the url and not locationID.
    // This check will find the city from partnerOfferingLocationID for day offices
    if (
      queryParams.partnerOfferingLocationID &&
      !queryParams.city &&
      offeringLocations &&
      !queryParams.locationID &&
      !queryParams.spaceID
    ) {
      const foundOfferingLocation = offeringLocations.find(
        (loc) => loc.id === queryParams.partnerOfferingLocationID
      );
      if (foundOfferingLocation) {
        const search = new URLSearchParams(location.search);
        search.append("city", foundOfferingLocation.location.city);
        history.replace(
          `${location.pathname}?${search.toString()}`,
          location.state
        );
      }
    }
    // adding toast here causes infinite rerender.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    history,
    location.pathname,
    location.search,
    offeringLocations,
    template,
    queryParams.city,
    queryParams.locationID,
    offeringTypeString,
  ]);
}

interface UseAppendMembershipCityFromURLLocationConfig {
  offeringLocations: PartnerOfferingLocation[];
  isCityPickerHidden: boolean;
  handleChangeCity: (city: string) => void;
  handleChangeLocation: (locationID: string) => void;
}

export function useAppendMembershipCityFromURLLocation({
  offeringLocations,
  isCityPickerHidden,
  handleChangeCity,
  handleChangeLocation,
}: UseAppendMembershipCityFromURLLocationConfig) {
  const queryParams = useQueryString();
  const toast = useToast();
  const [toastShowed, setToastShowed] = useState(false);

  useEffect(() => {
    if (
      queryParams.locationID &&
      !queryParams.city &&
      !isCityPickerHidden &&
      offeringLocations
    ) {
      const foundOfferingLocation = offeringLocations.find(
        (loc) =>
          isPartnerLocation(loc.membership?.locatable) &&
          loc.membership?.locatable?.id === queryParams.locationID
      );

      if (
        foundOfferingLocation &&
        isPartnerLocation(foundOfferingLocation.membership?.locatable)
      ) {
        handleChangeCity(foundOfferingLocation.membership?.locatable?.city);
        handleChangeLocation(foundOfferingLocation.membership.locatable.id);
      } else if (!toastShowed) {
        toast.warning({
          message: "The pre-selected location does not have membership",
        });
        setToastShowed(true);
      }
    }
  }, [
    offeringLocations,
    queryParams.city,
    queryParams.locationID,
    isCityPickerHidden,
    handleChangeCity,
    handleChangeLocation,
    toastShowed,
    toast,
  ]);
}
